/**
 * ChatListItem (06 §4.1) — 72px row: avatar · name+preview · time+badge.
 * Preview prefixes media glyphs, draft, and own-message tick. Typing replaces
 * preview with green "typing…". Right-click → context menu.
 */
import { useState } from 'react';
import { BellOff, Pin } from 'lucide-react';
import type { Conversation } from '../../data/model';
import { useChatStore } from '../../stores/chatStore';
import { useUiStore } from '../../stores/uiStore';
import { listTime } from '../../lib/format';
import { Avatar } from '../primitives/Avatar';
import { TickIcon } from '../conversation/TickIcon';
import { UnreadBadge } from './UnreadBadge';
import styles from './ChatListItem.module.css';

interface Props {
  conv: Conversation;
  active: boolean;
  onOpen: () => void;
}

/** Stable empty array so the zustand snapshot reference never changes when a
 *  conversation has no typing users (avoids getSnapshot-not-cached re-render loop). */
const NO_TYPING: string[] = [];

function mediaGlyph(contentType: string): string | null {
  switch (contentType) {
    case 'image':
      return '📷 Photo';
    case 'video':
      return '🎬 Video';
    case 'audio':
      return '🎤 Voice message';
    case 'file':
      return '📄 Document';
    default:
      return null;
  }
}

export function ChatListItem({ conv, active, onOpen }: Props) {
  const me = useChatStore((s) => s.me);
  const userById = useChatStore((s) => s.userById);
  const typingUsers = useChatStore((s) => s.typing[conv.id] ?? NO_TYPING);
  const togglePin = useChatStore((s) => s.togglePin);
  const toggleMute = useChatStore((s) => s.toggleMute);
  const toggleArchive = useChatStore((s) => s.toggleArchive);
  const markRead = useChatStore((s) => s.markRead);
  const toast = useUiStore((s) => s.toast);

  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);

  const peer = conv.type === 'direct' && conv.peerId ? userById(conv.peerId) : undefined;
  const title = conv.type === 'group' ? conv.title ?? 'Group' : peer?.displayName ?? 'Unknown';
  const avatarId = conv.type === 'group' ? conv.id : peer?.id ?? conv.id;

  const last = conv.lastMessage;
  const isTyping = typingUsers.length > 0;

  let previewNode: React.ReactNode = <span className={styles.muted}>No messages yet</span>;
  if (isTyping) {
    const who =
      conv.type === 'group' && typingUsers[0]
        ? `${userById(typingUsers[0])?.displayName?.split(' ')[0] ?? 'Someone'} is typing…`
        : 'typing…';
    previewNode = <span className={styles.typing}>{who}</span>;
  } else if (conv.draft.trim()) {
    previewNode = (
      <>
        <span className={styles.draft}>Draft: </span>
        <span className={styles.previewText}>{conv.draft}</span>
      </>
    );
  } else if (last) {
    const glyph = mediaGlyph(last.contentType);
    const text = last.deleted
      ? '🚫 This message was deleted'
      : glyph ?? last.text;
    const isMine = last.senderId === me.id;
    const groupPrefix =
      conv.type === 'group' && !isMine && last.senderId
        ? `${userById(last.senderId)?.displayName?.split(' ')[0] ?? ''}: `
        : '';
    previewNode = (
      <>
        {isMine && !last.deleted && <TickIcon status={last.status} />}
        <span className={styles.previewText}>
          {groupPrefix}
          {text}
        </span>
      </>
    );
  }

  return (
    <li>
      <button
        className={`${styles.row} ${active ? styles.active : ''}`}
        onClick={onOpen}
        onContextMenu={(e) => {
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY });
        }}
        aria-current={active ? 'true' : undefined}
      >
        <Avatar id={avatarId} name={title} url={conv.avatarUrl} size={49} />
        <span className={styles.center}>
          <span className={styles.nameRow}>
            <span className={styles.name}>{title}</span>
          </span>
          <span className={styles.preview}>{previewNode}</span>
        </span>
        <span className={styles.right}>
          <span className={`${styles.time} ${conv.unreadCount > 0 ? styles.timeUnread : ''}`}>
            {last ? listTime(conv.updatedAtMs) : ''}
          </span>
          <span className={styles.indicators}>
            {conv.pinned && <Pin size={15} className={styles.pin} aria-label="Pinned" />}
            {conv.mutedUntilMs && <BellOff size={15} className={styles.bell} aria-label="Muted" />}
            <UnreadBadge count={conv.unreadCount} />
          </span>
        </span>
      </button>

      {menu && (
        <>
          <div className={styles.menuScrim} onClick={() => setMenu(null)} />
          <ul className={styles.menu} style={{ top: menu.y, left: menu.x }} role="menu">
            <li>
              <button
                role="menuitem"
                onClick={() => {
                  toggleArchive(conv.id);
                  setMenu(null);
                  toast(conv.archived ? 'Chat unarchived' : 'Chat archived');
                }}
              >
                {conv.archived ? 'Unarchive chat' : 'Archive chat'}
              </button>
            </li>
            <li>
              <button role="menuitem" onClick={() => { toggleMute(conv.id); setMenu(null); }}>
                {conv.mutedUntilMs ? 'Unmute' : 'Mute notifications'}
              </button>
            </li>
            <li>
              <button role="menuitem" onClick={() => { togglePin(conv.id); setMenu(null); }}>
                {conv.pinned ? 'Unpin chat' : 'Pin chat'}
              </button>
            </li>
            <li>
              <button
                role="menuitem"
                onClick={() => {
                  markRead(conv.id);
                  setMenu(null);
                  toast('Marked as read');
                }}
              >
                Mark as read
              </button>
            </li>
          </ul>
        </>
      )}
    </li>
  );
}
